import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePlanningAssistantStore } from '@/store/usePlanningAssistantStore';
import { fmtTime } from '../assistantFormat';

const AUTO_HIDE_MS = 4500;

/** Dark toast at the bottom centre after assigning, with an undo button. */
export function UndoToast() {
  const { t } = useTranslation('planning');
  const lastAssigned = usePlanningAssistantStore((s) => s.lastAssigned);
  const busy = usePlanningAssistantStore((s) => s.busy);
  const undo = usePlanningAssistantStore((s) => s.undo);
  const dismiss = usePlanningAssistantStore((s) => s.dismissLastAssigned);
  const seq = lastAssigned?.seq;

  // why: paused while an undo runs — a failed undo keeps the toast so it can be retried.
  useEffect(() => {
    if (seq == null || busy) return;
    const timer = window.setTimeout(dismiss, AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [seq, busy, dismiss]);

  if (!lastAssigned) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-20 md:bottom-5 left-1/2 -translate-x-1/2 z-[90] bg-slate-900 text-white rounded-lg pl-3 pr-1.5 py-1.5 flex items-center gap-3 text-[12.5px] max-w-[calc(100vw-32px)] shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]"
    >
      <Check size={14} strokeWidth={2} className="text-emerald-400 shrink-0" />
      <span className="tabular-nums truncate">
        {t('assistant.toast.assigned', {
          task: lastAssigned.taskName,
          employee: lastAssigned.employeeName,
          from: fmtTime(lastAssigned.start),
          to: fmtTime(lastAssigned.end),
        })}
      </span>
      <button
        type="button"
        onClick={() => void undo()}
        disabled={busy}
        className="px-2 py-1 rounded-md hover:bg-white/10 text-emerald-300 font-medium shrink-0 disabled:opacity-50"
      >
        {t('assistant.toast.undo')}
      </button>
    </div>,
    document.body,
  );
}
